import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { XCircle, X, AlertCircle, AlertTriangle } from 'lucide-react';
import { ROLE_LABELS } from '../../lib/constants';

const ENTITY_CONFIG = {
  PROFILE: { label: 'Profile', badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  PREQUALIFICATION: { label: 'Prequalification', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  TENDER: { label: 'Tender / Proposal', badge: 'bg-purple-50 text-purple-700 border-purple-200' },
};

export default function RejectRequestModal({ request, onConfirm, onClose }) {
  const requester = request?.requestedBy;
  const entityType = request?.entityType || 'PROFILE';
  const entityConfig = ENTITY_CONFIG[entityType] || { label: entityType, badge: 'bg-gray-100 text-gray-700 border-gray-200' };

  const [remarks, setRemarks] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!remarks.trim()) {
      setError('Please provide a reason or remarks for rejection.');
      return;
    }

    try {
      setIsSubmitting(true);
      await onConfirm({ remarks: remarks.trim() });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to reject request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalContent = (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[110] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg animate-scale-in border border-gray-100 overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center shrink-0 border border-rose-100">
              <XCircle className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${entityConfig.badge}`}>
                  {entityConfig.label}
                </span>
                <h3 className="text-base font-bold text-gray-900">Reject Request</h3>
              </div>
              <p className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">
                {request?.title || 'Approval Request'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Error */}
          {error && (
            <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Submitter Info */}
          <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200/70 flex items-center justify-between text-xs">
            <div>
              <span className="text-gray-400 font-medium block mb-0.5">Submitted by</span>
              <span className="font-semibold text-gray-800 text-sm">
                {requester?.username || 'Team Member'}
              </span>
              {requester?.role && (
                <span className="ml-2 px-2 py-0.5 rounded-full bg-slate-200 text-gray-600 text-[10px] font-medium">
                  {ROLE_LABELS[requester.role] || requester.role}
                </span>
              )}
            </div>
            <div className="text-right">
              <span className="text-gray-400 font-medium block mb-0.5">Current status</span>
              <span className="font-semibold text-amber-600">Pending Review</span>
            </div>
          </div>

          {/* Warning Notice */}
          <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3 text-xs text-amber-800">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <span className="font-semibold block mb-0.5">Will be returned to submitter</span>
              This request will be marked as <strong>Rejected</strong> and sent back to{' '}
              <strong>{requester?.username || 'the submitter'}</strong> with your revision comments.
            </div>
          </div>

          {/* Rejection Reason */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-700 flex items-center justify-between">
              <span>Reason for Rejection / Revision Comments</span>
              <span className="text-rose-500 font-bold">* Required</span>
            </label>
            <textarea
              value={remarks}
              onChange={(e) => {
                setRemarks(e.target.value);
                if (error) setError('');
              }}
              placeholder="Explain clearly why this request is being returned (missing documents, incorrect data, duplicate entry, etc.)..."
              rows={4}
              required
              maxLength={600}
              className="w-full text-xs rounded-xl border border-gray-200 p-3 text-gray-800 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-all resize-none"
            />
            <p className="text-[11px] text-gray-400">
              This reason will be visible to the submitter and recorded permanently in the audit history.
            </p>
          </div>

          {/* Footer Actions */}
          <div className="pt-1 flex items-center justify-end gap-2 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !remarks.trim()}
              className="px-5 py-2 text-xs font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-xl shadow-sm transition-all flex items-center gap-1.5 disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Rejecting...
                </>
              ) : (
                <>
                  <XCircle className="w-3.5 h-3.5" />
                  Reject & Return
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
